import { useMemo } from 'react';
import type { ReactNode } from 'react';

interface JurisprudenceRendererProps {
  content: string;
}

function cleanLinebreaks(content: string): string {
  // HTML comment form (from old text/html parser) — most common
  let cleaned = content.replace(/<!--\?linebreak\?-->/g, '<linebreak/>');
  // XML processing instruction form
  cleaned = cleaned.replace(/<\?linebreak\?>/g, '<linebreak/>');
  // HTML-encoded form (when content was stored with HTML entity encoding)
  cleaned = cleaned.replace(/&lt;\?linebreak\?&gt;/g, '<linebreak/>');
  return cleaned;
}

export function cleanUitspraakContent(content: string): string {
  return cleanLinebreaks(content);
}

export function JurisprudenceRenderer({ content }: JurisprudenceRendererProps) {
  const rendered = useMemo(() => {
    const cleaned = cleanUitspraakContent(content);
    return parseContent(cleaned);
  }, [content]);

  return (
    <div className="prose prose-sm max-w-none dark:prose-invert leading-relaxed text-sm">
      {rendered}
    </div>
  );
}

function parseContent(xmlString: string): ReactNode {
  // Sanitize HTML entities that are not valid in XML
  // &nbsp; and other HTML entities cause XML parsing errors
  const sanitized = xmlString
    .replace(/&nbsp;/g, ' ')
    .replace(/&mdash;/g, '\u2014')
    .replace(/&ndash;/g, '\u2013')
    .replace(/&lsquo;/g, '\u2018')
    .replace(/&rsquo;/g, '\u2019')
    .replace(/&ldquo;/g, '\u201C')
    .replace(/&rdquo;/g, '\u201D')
    .replace(/&amp;(?!(amp|lt|gt|quot|apos);)/g, '&amp;'); // Escape unescaped ampersands

  // Parse XML using DOMParser
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<root>${sanitized}</root>`, 'text/xml');

  // Check for parsing errors
  const parserError = doc.querySelector('parsererror');
  if (parserError) {
    return <div className="text-sm whitespace-pre-wrap">{xmlString}</div>;
  }

  // Create renderNode function with access to doc
  const renderNode = (node: Node, index: number = 0): ReactNode => {
  // Text node
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent?.trim();
    return text ? text : null;
  }

  // Element node
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element;
    const tagName = element.tagName.toLowerCase();
    const children = Array.from(element.childNodes).map((child, i) => renderNode(child, i));

    switch (tagName) {
      case 'root':
        return <>{children}</>;

      case 'uitspraak.info':
      case 'uitspraak':
        return <div className="space-y-3 mb-6">{children}</div>;

      case 'section':
        return (
          <section key={index} className="mb-8 mt-6 space-y-4">
            {children}
          </section>
        );

      case 'title': {
        // Title text may contain escaped <nr> tags as text
        const titleText = element.textContent || '';
        const nrMatch = titleText.match(/<nr>(.*?)<\/nr>/);

        if (nrMatch) {
          // Extract the number and the rest of the title
          const nr = nrMatch[1];
          const restOfTitle = titleText.replace(/<nr>.*?<\/nr>/, '').trim();

          return (
            <h3 key={index} className="font-bold text-lg mb-3 text-foreground flex gap-3">
              <span className="flex-shrink-0">{nr}</span>
              <span className="flex-1">{restOfTitle}</span>
            </h3>
          );
        }

        return (
          <h3 key={index} className="font-bold text-lg mb-3 text-foreground">
            {titleText}
          </h3>
        );
      }

      case 'bridgehead': {
        const isBold = element.getAttribute('role') === 'bold';
        // Only center if it's the main court title (all caps)
        const text = element.textContent || '';
        const shouldCenter = isBold && text === text.toUpperCase();
        return (
          <h2 key={index} className={`text-base mb-6 mt-4 ${shouldCenter ? 'text-center' : ''} ${isBold ? 'font-bold uppercase tracking-wide' : ''}`}>
            {children}
          </h2>
        );
      }

      case 'para': {
        // Check if this para contains other structural elements or just text
        const hasStructuralChildren = Array.from(element.children).some(child =>
          ['parablock', 'paragroup', 'para', 'itemizedlist', 'orderedlist'].includes(child.tagName.toLowerCase())
        );

        if (hasStructuralChildren) {
          // Container para - no margin
          return <div key={index}>{children}</div>;
        }
        // Text para - add margin
        return <p key={index} className="mb-2 leading-relaxed">{children}</p>;
      }

      case 'parablock':
        return <div key={index} className="mb-2">{children}</div>;

      case 'paragroup': {
        // Check if there's a <nr> child element
        const nrElement = element.querySelector('nr');
        const hasNr = !!nrElement;

        if (hasNr) {
          const nr = nrElement?.textContent || '';
          // Get all children except the nr element
          const otherChildren = Array.from(element.childNodes)
            .filter(child => {
              if (child.nodeType === Node.ELEMENT_NODE) {
                return (child as Element).tagName.toLowerCase() !== 'nr';
              }
              return child.nodeType === Node.TEXT_NODE && child.textContent?.trim();
            })
            .map((child, i) => renderNode(child, i));

          return (
            <div key={index} className="mb-4 flex gap-3">
              <span className="font-bold flex-shrink-0 min-w-[2rem]">{nr}</span>
              <div className="flex-1">{otherChildren}</div>
            </div>
          );
        }

        return <div key={index} className="mb-4">{children}</div>;
      }

      case 'nr':
        return <span key={index} className="font-bold mr-3 text-foreground">{children}</span>;

      case 'emphasis': {
        const role = element.getAttribute('role');
        if (role === 'bold') {
          return <strong key={index} className="mr-2">{children}</strong>;
        }
        if (role === 'italic') {
          return <em key={index}>{children}</em>;
        }
        if (role === 'underline') {
          return <u key={index}>{children}</u>;
        }
        return <span key={index}>{children}</span>;
      }

      case 'itemizedlist':
      case 'orderedlist': {
        const isOrdered = tagName === 'orderedlist';
        const ListTag = isOrdered ? 'ol' : 'ul';
        const listClass = isOrdered
          ? 'list-decimal ml-6 space-y-2 mb-4 pl-2'
          : 'list-disc ml-6 space-y-2 mb-4 pl-2';
        return (
          <ListTag key={index} className={listClass}>
            {children}
          </ListTag>
        );
      }

      case 'listitem':
        return <li key={index} className="ml-1 pl-2 leading-relaxed">{children}</li>;

      case 'footnote': {
        const footnoteId = element.getAttribute('id');
        const label = element.getAttribute('label');
        return (
          <div key={index} id={footnoteId || undefined} className="text-xs text-muted-foreground mt-2 pl-4 border-l-2 border-muted">
            <sup className="mr-1">{label}</sup>
            {children}
          </div>
        );
      }

      case 'footnote-ref': {
        const linkend = element.getAttribute('linkend');
        // Find the referenced footnote to get its label
        const referencedFootnote = linkend ? doc.querySelector(`footnote[id="${linkend}"]`) : null;
        const footnoteLabel = referencedFootnote?.getAttribute('label') || '?';
        return (
          <span key={index}>
            <sup>
              <a href={`#${linkend}`} className="text-primary hover:underline">
                [{footnoteLabel}]
              </a>
            </sup>
            {children}
          </span>
        );
      }

      case 'linebreak':
        return <br key={index} />;

      default:
        // For unknown tags, just render children
        return <span key={index}>{children}</span>;
    }
  }

  return null;
  };

  return renderNode(doc.documentElement);
}
